import React, { useState, useEffect, useCallback } from 'react'
import { Modal, Button } from 'semantic-ui-react';
import PropTypes from "prop-types";
import { RoomTypeAddon } from '../../../api/services/next';
import Transfer from '../../../components/shared/Transfer';
import { useTextResources } from '../../../language';
import { useLoader } from '../../../hooks/useLoader';
import { onError, onSuccess, onWarning } from '../../../components/shared/messages';

const RoomTypeAddonPopup = ({ controller, chainAddon }) => {
    const [open, setOpen] = useState(false);
    const text = useTextResources();
    const loader = useLoader();
    const [allRoomTypes, setAllRoomTypes] = useState([]);
    const [targetKeys, setTargetKeys] = useState([]);
    const [selectedChainAddon, setSelectedChainAddon] = useState(chainAddon);

    const searchSelector = useCallback((roomType, searchTerm) => {
        if (!searchTerm) return true; // If no search term, return all results

        // Split search term into individual words, trim spaces, and escape special characters
        const terms = searchTerm.trim().split(/\s+/).map(term => term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));

        // Create a regex that matches all terms in any order
        const regex = new RegExp(terms.map(term => `(?=.*${term})`).join(''), 'i');

        // Test the regex against the roomType.Name, ignoring extra spaces
        return regex.test(roomType.Name.replace(/\s+/g, ' ').trim());
      }, []);
    const keySelector = useCallback((roomType) => roomType.Id, []);
    const itemRender = useCallback((roomType) => `${roomType.Name}`, []);

    useEffect(() => {
        (async () => {
            if (controller) {
                window[controller] = (action, params) => {
                    switch (action) {
                        case "open":
                            if (params) {
                                setSelectedChainAddon(params.chainAddon)
                                validateSelectAddonAndLoadRoomTypes(params.chainAddon)
                            }

                            break;
                        default:
                            break;
                    }
                }
            }
            else if (selectedChainAddon){
                setOpen(true)
                validateSelectAddonAndLoadRoomTypes(selectedChainAddon)
            }
        })()
    }, [])

    const validateSelectAddonAndLoadRoomTypes = async (chainAddon) => {
        loader(true);
        try {
            const addonPricedInNonAdddonRateCount = await RoomTypeAddon.AddonPricedInNonAdddonRateCount({ chainAddonId: chainAddon.Id });
            if (Number(addonPricedInNonAdddonRateCount)) {
                onWarning(text('SelectedAddonIsAlreadyPriced'))
                closePopup();
            }
            else {
                setOpen(true);
                const data = await RoomTypeAddon.GetRoomTypeAddon({
                    chainAddonId: chainAddon.Id
                });
                setAllRoomTypes(data.roomTypes);
                setTargetKeys(data.roomTypeAddons.map(rta => rta.RoomTypeId))
            }

        } catch (error) {
            error.status === 400 ? onWarning(error) : onError(error)
        }
        finally {
            loader(false)
        }
    }

    const save = async (close) => {
        loader(true);
        try {
            const request = targetKeys?.length ?
                targetKeys.map(k => { return { ChainAddonId: selectedChainAddon.Id, RoomTypeId: parseInt(k) } }) :
                [{ ChainAddonId: selectedChainAddon.Id, RoomTypeId: 0 }]

            await RoomTypeAddon.UpdateRoomTypeAddons(request);

            onSuccess();
            if (close) closePopup();
        } catch (error) {
            error.status === 400 ? onWarning(error) : onError(error)
        }
        finally {
            loader(false)
        }
    }

    const closePopup = () => {
        setTargetKeys([]);
        setAllRoomTypes([]);
        setOpen(false);
    }

    return (
        <Modal size="large" open={open} style={{ zIndex: 1000000, minWidth: screen.height }} >
            <Modal.Header>{`${text("AddonAndRoomTypeMapping")} ${selectedChainAddon?.Name}`}</Modal.Header>
            <Modal.Content>
                <Transfer
                    items={allRoomTypes}
                    itemKeySelector={keySelector}
                    itemRender={itemRender}
                    targetKeys={targetKeys}
                    hasSearch
                    searchSelector={searchSelector}
                    hasSelectAll
                    onTargetKeysChange={setTargetKeys}
                />
            </Modal.Content>
            <Modal.Actions>
                <Button size={"small"} negative onClick={closePopup}>{text("Close")}</Button>
                <Button size={"small"} positive onClick={() => { save(true) }}>{text("Saveandclose")}</Button>
                <Button size={"small"} positive onClick={() => { save(false) }}>{text("Save")}</Button>
            </Modal.Actions>
        </Modal>
    )
}

RoomTypeAddonPopup.propTypes = {
    controller: PropTypes.any,
    chainAddon: PropTypes.object,
    open: PropTypes.bool
}

export default RoomTypeAddonPopup
